import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import { IconX } from "@tabler/icons-react";
import { usePointerStore } from "@/lib/onboarding/pointer-store";
import { useCoachStore } from "@/lib/onboarding/coach-store";
import { waitForAnchor } from "./tour/waitForAnchor";
import "./onboarding-coach.css";

type Placement = "top" | "bottom" | "left" | "right";

/** Distance between the target's edge and the card (leaves room for the caret). */
const GAP = 12;
/** Minimum inset the card keeps from the viewport edges. */
const MARGIN = 8;
/** Side length of the square that gets rotated 45° into the caret. */
const ARROW = 10;
/** Keep the caret this far away from the card's rounded corners. */
const ARROW_INSET = 18;

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

/* The caret is a rotated square sharing the card's surface; per placement only
   the two borders facing AWAY from the card may paint, so the seam where it
   overlaps the card stays invisible. */
const ARROW_CLASSES: Record<Placement, string> = {
  bottom: "border-t border-l", // card below target, caret points up
  top: "border-b border-r", // card above target, caret points down
  right: "border-l border-b", // card right of target, caret points left
  left: "border-t border-r", // card left of target, caret points right
};

/**
 * Stripe-style anchored pointer card for guided onboarding steps. Mounted once
 * at App level (survives route changes); armed via the pointer store by the
 * step detail overlay right before it navigates. Waits for the (lazy-mounted)
 * target, then attaches a compact instruction card with a caret to it —
 * auto-placed bottom/top/right/left with viewport collision handling, tracking
 * the target every frame (scroll, resize, layout shifts). The page stays fully
 * interactive; the only target emphasis is the existing 2px pulse ring.
 *
 * Ends when the user clicks the target (advancing multi-step chains first),
 * navigates to a different route, dismisses it, or presses Escape. Hides —
 * without ending the chain — while a blocking modal is open (coach store
 * overlayCount), so it can never cover a modal.
 */
export const OnboardingPointer: React.FC = () => {
  const stepKey = usePointerStore((s) => s.stepKey);
  const steps = usePointerStore((s) => s.steps);
  const index = usePointerStore((s) => s.index);
  const overlayOpen = useCoachStore((s) => s.overlayCount > 0);
  const t = useTranslations("onboarding");
  const pathname = useLocation().pathname;

  const active = stepKey !== null ? (steps[index] ?? null) : null;
  const selector = active?.selector ?? null;

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<Placement>("bottom");
  const cardRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLSpanElement | null>(null);
  // Pathname the anchor was found on — leaving that route ends the chain.
  const armedPathRef = useRef<string | null>(null);
  // The anchor we've already scrolled to, so the layout effect re-running on an
  // overlayOpen toggle doesn't re-scroll the page behind the modal.
  const scrolledForRef = useRef<HTMLElement | null>(null);

  // Acquire the target once the destination page has mounted it.
  useEffect(() => {
    setAnchor(null);
    armedPathRef.current = null;
    if (!selector) return;
    let cancelled = false;
    waitForAnchor(selector, { timeoutMs: 10000 }).then((el) => {
      if (cancelled) return;
      if (!el) {
        // Target never appeared (role gate, empty state variant, …) — bail
        // quietly instead of leaving a stale chain armed for a later page.
        usePointerStore.getState().stopPointer();
        return;
      }
      armedPathRef.current = window.location.pathname;
      setAnchor(el);
    });
    return () => {
      cancelled = true;
    };
  }, [selector, stepKey, index]);

  // Navigating away from the page the card is anchored on ends the chain.
  useEffect(() => {
    if (anchor && armedPathRef.current && pathname !== armedPathRef.current) {
      usePointerStore.getState().stopPointer();
    }
  }, [pathname, anchor]);

  // Attach to the anchor: pulse ring, click-to-advance, per-frame tracking.
  // Layout effect so the first position lands before paint (no 0,0 flash).
  useLayoutEffect(() => {
    if (!anchor || !selector) return;
    // Only scroll the first time we attach to THIS anchor — not on every
    // overlayOpen re-run (which would yank the page around behind a modal).
    if (scrolledForRef.current !== anchor) {
      anchor.scrollIntoView({ block: "center", behavior: "smooth" });
      scrolledForRef.current = anchor;
    }
    anchor.classList.add("onboarding-coach-pulse");

    // Capture phase so a stopPropagation inside the target can't swallow the
    // handoff — the moment the user does what the card asks, it steps aside.
    const onTargetClick = () => usePointerStore.getState().advancePointer();
    anchor.addEventListener("click", onTargetClick, true);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") usePointerStore.getState().stopPointer();
    };
    window.addEventListener("keydown", onKey);

    let raf = 0;
    let disposed = false;

    const position = () => {
      const card = cardRef.current;
      if (!card) return;
      const r = anchor.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Target scrolled fully out of view → hide the card instead of pinning it
      // to a viewport edge with its caret aimed at nothing. `visibility` is
      // independent of the framer opacity animation on the same element.
      if (r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) {
        card.style.visibility = "hidden";
        return;
      }
      card.style.visibility = "visible";
      const cw = card.offsetWidth;
      const ch = card.offsetHeight;
      const fits: Record<Placement, boolean> = {
        bottom: r.bottom + GAP + ch <= vh - MARGIN,
        top: r.top - GAP - ch >= MARGIN,
        right: r.right + GAP + cw <= vw - MARGIN,
        left: r.left - GAP - cw >= MARGIN,
      };
      const place =
        (["bottom", "top", "right", "left"] as Placement[]).find((p) => fits[p]) ??
        "bottom";

      let x: number;
      let y: number;
      if (place === "bottom" || place === "top") {
        x = clamp(r.left + r.width / 2 - cw / 2, MARGIN, vw - MARGIN - cw);
        y = place === "bottom" ? r.bottom + GAP : r.top - GAP - ch;
      } else {
        y = clamp(r.top + r.height / 2 - ch / 2, MARGIN, vh - MARGIN - ch);
        x = place === "right" ? r.right + GAP : r.left - GAP - cw;
      }
      card.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;

      const arrow = arrowRef.current;
      if (arrow) {
        // Aim the caret at the target's center, clamped clear of the corners.
        if (place === "bottom" || place === "top") {
          const ax = clamp(r.left + r.width / 2 - x, ARROW_INSET, cw - ARROW_INSET);
          arrow.style.left = `${Math.round(ax - ARROW / 2)}px`;
          arrow.style.top = place === "bottom" ? `${-ARROW / 2}px` : "";
          arrow.style.bottom = place === "top" ? `${-ARROW / 2}px` : "";
          arrow.style.right = "";
        } else {
          const ay = clamp(r.top + r.height / 2 - y, ARROW_INSET, ch - ARROW_INSET);
          arrow.style.top = `${Math.round(ay - ARROW / 2)}px`;
          arrow.style.left = place === "right" ? `${-ARROW / 2}px` : "";
          arrow.style.right = place === "left" ? `${-ARROW / 2}px` : "";
          arrow.style.bottom = "";
        }
      }
      setPlacement((prev) => (prev === place ? prev : place));
    };

    const track = () => {
      if (disposed) return;
      if (!anchor.isConnected) {
        // The node got swapped by a re-render / breakpoint change. Give a
        // replacement a short window to mount, then re-attach or end.
        waitForAnchor(selector, { timeoutMs: 2500 }).then((el) => {
          if (disposed) return;
          if (el) setAnchor(el);
          else usePointerStore.getState().stopPointer();
        });
        return;
      }
      position();
      raf = requestAnimationFrame(track);
    };
    track();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      anchor.classList.remove("onboarding-coach-pulse");
      anchor.removeEventListener("click", onTargetClick, true);
      window.removeEventListener("keydown", onKey);
    };
    // overlayOpen intentionally re-runs the effect: while the card is hidden
    // the rAF loop has no card to place; on reveal this re-positions before
    // paint instead of one frame late.
  }, [anchor, selector, overlayOpen]);

  const visible = active !== null && anchor !== null && !overlayOpen;

  return createPortal(
    <AnimatePresence>
      {visible && (
        /* Positioned imperatively (style.transform, per-frame). The motion
           wrapper animates ONLY opacity so framer never fights that transform. */
        <motion.div
          key={`${stepKey}-${index}`}
          ref={cardRef}
          className="fixed left-0 top-0 z-[290]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16, ease: "easeOut" }}
        >
          <div className="relative surface-card border outline-secondary rounded-xl shadow-lg max-w-72 pl-3.5 pr-2 py-2.5">
            <span
              ref={arrowRef}
              aria-hidden
              className={`absolute w-2.5 h-2.5 rotate-45 surface-card outline-secondary ${ARROW_CLASSES[placement]}`}
            />
            <div className="relative flex items-start gap-2">
              <div className="min-w-0 pt-0.5">
                {steps.length > 1 && (
                  <span className="body-4-medium font-tertiary block mb-0.5 tabular-nums">
                    {index + 1}/{steps.length}
                  </span>
                )}
                <p className="body-3-regular font-primary">
                  {active ? t(active.textKey) : null}
                </p>
              </div>
              <button
                type="button"
                onClick={() => usePointerStore.getState().stopPointer()}
                aria-label={t("dismiss")}
                className="p-1 -mt-0.5 rounded-md hover:surface-tertiary transition-colors cursor-pointer shrink-0"
              >
                <IconX size={14} className="font-tertiary" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
